import { BaseComponent, IBaseComponentProps } from "../BaseComponent/BaseComponent";
import { DataControl } from "../DataControl/DataControl";


export interface IDataPanelProps<TData> extends IBaseComponentProps
{
    data?: TData;
}

export interface IDataControlDefinition
{
    Control: DataControl,
    DefaultValue?;
    GetRaw?: boolean;
    GetKeyOverride?: string;
}

export abstract class DataPanel<TData, T extends IDataPanelProps<TData> = IDataPanelProps<TData>> extends BaseComponent<T>
{
    private _dataControlMap: IMap<IDataControlDefinition> = {} as IMap<IDataControlDefinition>;
    private _dataPanelMap: IMap<DataPanel<unknown, unknown>> = {} as IMap<DataPanel<unknown, unknown>>;
    private _keylessDataPanels: DataPanel<unknown, unknown>[] = [];
    private _dataPropertyMap: IMap<any> = {} as IMap<any>;
    private _firstControl: DataPanel<unknown, unknown> | DataControl;
    
    constructor(props: T)
    {
        super(props)
    }

    public override componentDidMount()
    {
        if (this.props.data)
        {
            this.setData(this.props.data);
        }
        super.componentDidMount();
    }

    public setData(data: TData)
    {
        data = data || {} as TData;
        for (let dataKey in this._dataControlMap)
        {
            this._dataControlMap[dataKey].Control.setValue(data[dataKey]);
        }

        for (let dataKey in this._dataPanelMap)
        {
            this._dataPanelMap[dataKey].setData(data[dataKey]);
        }

        for (let property in this._dataPropertyMap)
        {
            this._dataPropertyMap[property] = data[property];
        }

        for (let dataPanel of this._keylessDataPanels)
        {
            dataPanel.setData(data);
        }
    }

    public getData(data?: TData): TData
    {
        data = data || {} as TData;

        for (let dataKey in this._dataControlMap)
        {
            let definition = this._dataControlMap[dataKey];

            data[definition.GetKeyOverride || dataKey] = definition.GetRaw ? definition.Control.getValueRaw() : definition.Control.getValue();
        }

        for (let dataPanel of this._keylessDataPanels)
        {
            let newData = dataPanel.getData();
            if (newData)
            {
                $.extend(data, newData);
            }
        }

        for (let dataKey in this._dataPanelMap)
        {
            let dataPanel = this._dataPanelMap[dataKey];
            data[dataKey] = dataPanel.getData();
        }

        for (let property in this._dataPropertyMap)
        {
            data[property] = this._dataPropertyMap[property];
        }

        return data;
    }

    protected addDataControl(control: DataControl, key:string & keyof TData, getKeyOverride?: string, defaultValue?, getRaw?: boolean)
    {
        if (control)
        {
            if (!this._firstControl)
            {
                this._firstControl = control;
            }
            this._dataControlMap[key] = { Control: control, DefaultValue: defaultValue, GetRaw: getRaw, GetKeyOverride: getKeyOverride };
        }
    }

    protected addDataPanel(dataPanel: DataPanel<unknown, unknown>, key?: string & keyof TData)
    {
        if (!this._firstControl)
        {
            this._firstControl = dataPanel;
        }

        if (!key)
        {
            this._keylessDataPanels.push(dataPanel);
        }
        else
        {
            this._dataPanelMap[key] = dataPanel;
        }
    }

    protected addDataProperty(key: string & keyof TData, value?)
    {
        this._dataPropertyMap[key] = (value == null) ? null: value;
    }
    

    public focus()
    {
        if (this._firstControl)
        {
            this._firstControl.focus();
        }
    }

    public clearData()
    {
        for (var dataKey in this._dataControlMap)
        {
            let definition = this._dataControlMap[dataKey];
            if (typeof (definition.DefaultValue) !== "undefined")
            {
                definition.Control.setValue(definition.DefaultValue);
            }
            else
            {
                definition.Control.setValue(null);
            }
        }

        for (let dataKey in this._dataPanelMap)
        {
            this._dataPanelMap[dataKey].clearData();
        }

        for (let property in this._dataPropertyMap)
        {
            this._dataPropertyMap[property] = null;
        }
    }
}